import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Eye, EyeOff } from 'lucide-react';
import { AppSelect } from '../../../../components/common/select';
import BusinessLocationAutocomplete from './BusinessLocationAutocomplete/BusinessLocationAutocomplete';
import useModalEffect from '../../../../hooks/useModalEffect';

const CompanyEdit = ({
  isOpen,
  onClose,
  onSubmit,
  editingId,
  companyFormData,
  companyErrors,
  loading,
  handleCompanyInputChange,
  handleCompanySelectChange,
  handleContactInputChange,
  handleContactLoginToggle,
  handleAddContact,
  removeContact,
  partnerTypes,
  entityTypes,
  sectors,
  currencies,
  branches = [],
  taggingOptions,
  duplicateCompanies = [],
  similarCompanies = [],
  isCheckingDuplicates = false
}) => {
  const [showPassword, setShowPassword] = useState([]);
  const [showConfirmPassword, setShowConfirmPassword] = useState([]);

  useModalEffect(isOpen);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const handleGeneratePasswordToggle = (index, checked) => {
    // Update the contact's generatePassword field
    const event = {
      target: {
        name: `contacts[${index}].generatePassword`,
        value: checked
      }
    };
    handleContactInputChange(index, 'generatePassword', checked);
  };

  return (
    <>
      <style>{`
        #modal-companies .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-companies .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-companies .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-companies .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-companies .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-companies .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`}
        id="modal-companies"
        style={{ display: isOpen ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
        aria-hidden={!isOpen}
      >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{editingId ? "Edit Company" : "New Company"}</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
            </div>

            <div className="modal-body">
              <div className="row">
                {/* Informasi perusahaan */}
                <div className="col-lg-12">
                  <div className="inv-section-label">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    Company Information
                  </div>
                </div>
                <div className="col-lg-6">
                  <AppSelect
                    name="businessPartnerType"
                    value={companyFormData.businessPartnerType}
                    onChange={handleCompanySelectChange}
                    options={partnerTypes}
                    placeholder="Select partner type"
                    label="Business Partner Type"
                    required
                    error={companyErrors.businessPartnerType}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                  />
                </div>

                <div className="col-lg-6">
                  <AppSelect
                    name="entityType"
                    value={companyFormData.entityType}
                    onChange={handleCompanySelectChange}
                    options={entityTypes}
                    placeholder="Select entity type"
                    label="Entity Type"
                    required
                    error={companyErrors.entityType}
                    valueField="id"
                    labelField="entityType"
                    searchField="entityType"
                  />
                </div>

                <div className="col-lg-6">
                    <label htmlFor="companyName" className="form-label">Company Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${companyErrors.companyName ? 'is-invalid' : ''}`}
                      id="companyName"
                      name="companyName"
                      placeholder="Enter company name"
                      value={companyFormData.companyName || ''}
                      onChange={handleCompanyInputChange}
                      autoComplete="organization"
                    />
                    {companyErrors.companyName && <div className="invalid-feedback">{companyErrors.companyName}</div>}
                    {duplicateCompanies.length > 0 && !companyErrors.companyName && (
                      <small className="text-danger d-block mt-1">
                        A company with this exact name already exists. Please choose a different name.
                      </small>
                    )}
                    {similarCompanies.length > 0 && duplicateCompanies.length === 0 && !companyErrors.companyName && (
                      <small className="text-warning d-block mt-1">
                        Similar company names found:{' '}
                        {similarCompanies.slice(0, 3).map((company, i) => (
                          <span key={company.id || i}>
                            <strong>{company.name}</strong>
                            {i < Math.min(similarCompanies.length, 3) - 1 ? ', ' : ''}
                          </span>
                        ))}
                        {similarCompanies.length > 3 && (
                          <span> and {similarCompanies.length - 3} more</span>
                        )}
                        . Please verify it is not a duplicate entry.
                      </small>
                    )}
                    {isCheckingDuplicates && (companyFormData.companyName || '').trim().length > 2 && (
                      <small className="text-muted d-block mt-1">
                        Checking for duplicate company names...
                      </small>
                    )}
                </div>

                <div className="col-lg-6">
                  <AppSelect
                    name="sector"
                    value={companyFormData.sector}
                    onChange={handleCompanySelectChange}
                    options={sectors}
                    placeholder="Select sector"
                    label="Sector"
                    required
                    error={companyErrors.sector}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                  />
                </div>
                <div className="col-lg-6">
                  <AppSelect
                    name="companySize"
                    value={companyFormData.companySize}
                    onChange={handleCompanySelectChange}
                    options={[
                      { id: '1', name: '1 - 5' },
                      { id: '2', name: '6 - 10' },
                      { id: '3', name: '11 - 15' },
                      { id: '4', name: '16 - 25' },
                      { id: '5', name: '26 - 45' },
                      { id: '6', name: '46 - 65' },
                      { id: '7', name: '66 - 85' },
                      { id: '8', name: '86 - 125' },
                      { id: '9', name: '126 - 175' },
                      { id: '10', name: '176 - 275' },
                      { id: '11', name: '276 - 425' },
                      { id: '12', name: '426 - 625' },
                      { id: '13', name: '626 - 875' },
                      { id: '14', name: '876 - 1175' },
                      { id: '15', name: '1176 - 1550' },
                      { id: '16', name: '1551 - 2025' },
                      { id: '17', name: '2026 - 2675' },
                      { id: '18', name: '2676 - 3450' },
                      { id: '19', name: '3451 - 4350' },
                      { id: '20', name: '4351 - 5450' },
                      { id: '21', name: '5451 - 6800' },
                      { id: '22', name: '6801 - 8500' },
                      { id: '23', name: '8501 - 10700' },
                      { id: '24', name: '> 10700' }
                    ]}
                    placeholder="Select company size"
                    label="Company Size"
                    required
                    error={companyErrors.companySize}
                  />
                </div>

                <div className="col-lg-6">
                  <div className="mb-3">
                  <label htmlFor="website" className="form-label">Website</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.website ? 'is-invalid' : ''}`}
                    id="website"
                    name="website"
                    placeholder="Enter website"
                    value={companyFormData.website || ''}
                    onChange={handleCompanyInputChange}
                    autoComplete="url"
                  />
                  {companyErrors.website && <div className="invalid-feedback">{companyErrors.website}</div>}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="mb-3">
                  <label htmlFor="companyEmail" className="form-label">Company Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="companyEmail"
                    name="companyEmail"
                    placeholder="Enter company email"
                    value={companyFormData.companyEmail || ''}
                    onChange={handleCompanyInputChange}
                    autoComplete="email"
                  />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="mb-3">
                  <label htmlFor="phone" className="form-label">Phone <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    className={`form-control ${companyErrors.phone ? 'is-invalid' : ''}`}
                    id="phone"
                    name="phone"
                    placeholder="Enter phone number"
                    value={companyFormData.phone || ''}
                    onChange={handleCompanyInputChange}
                    autoComplete="tel"
                  />
                  {companyErrors.phone && <div className="invalid-feedback">{companyErrors.phone}</div>}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="mb-3">
                  <label htmlFor="npwp" className="form-label">Company Tax Number/NPWP</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.npwp ? 'is-invalid' : ''}`}
                    id="npwp"
                    name="npwp"
                    placeholder="Enter company tax number/NPWP"
                    value={companyFormData.npwp || ''}
                    onChange={handleCompanyInputChange}
                    autoComplete="off"
                  />
                  {companyErrors.npwp && <div className="invalid-feedback">{companyErrors.npwp}</div>}
                  </div>
                </div>

                <div className="col-lg-6">
                  <AppSelect
                    key={`branch-${isOpen}-${branches.length}`}
                    name="branchId"
                    value={companyFormData.branchId === undefined || companyFormData.branchId === null ? '' : String(companyFormData.branchId)}
                    onChange={handleCompanySelectChange}
                    options={branches}
                    placeholder="Select branch"
                    label="Branch"
                    required={true}
                    error={companyErrors.branchId}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    autoComplete="off"
                  />
                  <div className="form-hint">Required when creating a new company</div>
                </div>

                <div className="col-lg-6">
                  <AppSelect
                    name="taggingIds"
                    value={Array.isArray(companyFormData.taggingIds) ? companyFormData.taggingIds : []}
                    onChange={handleCompanySelectChange}
                    options={taggingOptions}
                    placeholder="Select tagging(s)"
                    label="Tagging"
                    required={false}
                    error={companyErrors.taggingIds}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    autoComplete="off"
                    multiple={true}
                  />
                  <div className="form-hint">Optional: One or more tags for this company</div>
                </div>

                {/* Autocomplete lokasi bisnis */}
                {/* <div className="col-lg-12 mt-3">
                  <BusinessLocationAutocomplete
                    formData={companyFormData}
                    setFormData={(updater) => {
                      // Handle both function and object updates
                      const newData = typeof updater === 'function' ? updater(companyFormData) : updater;
                      // Update each field individually using handleCompanyInputChange
                      Object.entries(newData).forEach(([key, value]) => {
                        if (key !== 'contacts' && companyFormData[key] !== value) {
                          const event = {
                            target: {
                              name: key,
                              value: value
                            }
                          };
                          handleCompanyInputChange(event);
                        }
                      });
                    }}
                    errors={companyErrors}
                  />
                </div> */}

                {/* Address dan kode pos */}
                <div className="col-lg-8 mt-3">
                  <label htmlFor="address" className="form-label">Address <span className="text-danger">*</span></label>
                  <textarea
                    className={`form-control ${companyErrors.address ? 'is-invalid' : ''}`}
                    id="address"
                    name="address"
                    placeholder="Enter address"
                    value={companyFormData.address || ''}
                    onChange={handleCompanyInputChange}
                    rows="3"
                    autoComplete="street-address"
                  />
                  {companyErrors.address && <div className="invalid-feedback">{companyErrors.address}</div>}
                </div>

                <div className="col-lg-4 mt-3">
                  <label htmlFor="postalCode" className="form-label">Postal Code</label>
                  <input
                    type="text"
                    className="form-control"
                    id="postalCode"
                    name="postalCode"
                    placeholder="Enter postal code"
                    value={companyFormData.postalCode || ''}
                    onChange={handleCompanyInputChange}
                    autoComplete="postal-code"
                  />
                </div>

                <div className="col-lg-4 mt-3">
                  <label htmlFor="country" className="form-label">Country</label>
                  <input
                    type="text"
                    className="form-control"
                    id="country"
                    name="country"
                    value={companyFormData.country || ''}
                    onChange={handleCompanyInputChange}
                    placeholder="Enter country"
                    autoComplete="country"
                  />
                </div>

                <div className="col-lg-4 mt-3">
                  <label htmlFor="state" className="form-label">State</label>
                  <input
                    type="text"
                    className="form-control"
                    id="state"
                    name="state"
                    value={companyFormData.state || ''}
                    onChange={handleCompanyInputChange}
                    placeholder="Enter state"
                    autoComplete="address-level1"
                  />
                </div>

                <div className="col-lg-4 mt-3">
                  <label htmlFor="city" className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={companyFormData.city || ''}
                    onChange={handleCompanyInputChange}
                    placeholder="Enter city"
                    autoComplete="address-level2"
                  />
                </div>

                {/* Bagian Tambah/Edit Kontak */}
                <div className="col-12 mt-4">
                  <div className="inv-section-label">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Contact Person
                  </div>
                  {!(Array.isArray(companyFormData?.contacts) && companyFormData.contacts.length > 0) && (
                    <p className="text-muted small mb-2">
                      No contacts yet. This can happen when editing a company from a lead that has no contact data. Click &quot;Add Contact&quot; below to add one.
                    </p>
                  )}
                  {(Array.isArray(companyFormData?.contacts) ? companyFormData.contacts : []).map((contact, index) => {
                    const c = contact ?? {};
                    return (
                    <div className="card mb-4" key={index}>
                      <div className="card-header">
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <h6 className="card-title mb-0">Contact Person #{index + 1}</h6>
                          <button
                            type="button"
                            className="btn btn-icon btn-ghost-danger ms-auto"
                            onClick={() => removeContact(index)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M4 7l16 0"></path>
                              <path d="M10 11l0 6"></path>
                              <path d="M14 11l0 6"></path>
                              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="mb-3">
                              <label htmlFor={`contact-firstName-${index}`} className="form-label">First Name <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className={`form-control ${companyErrors?.contacts?.[index]?.firstName ? 'is-invalid' : ''}`}
                                id={`contact-firstName-${index}`}
                                name={`contacts[${index}].firstName`}
                                placeholder="Enter first name"
                                value={c.firstName ?? ''}
                                onChange={(e) => handleContactInputChange(index, 'firstName', e.target.value)}
                                autoComplete="given-name"
                              />
                              {companyErrors?.contacts?.[index]?.firstName && (
                                <div className="invalid-feedback">{companyErrors.contacts?.[index]?.firstName}</div>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="mb-3">
                              <label htmlFor={`contact-lastName-${index}`} className="form-label">Last Name <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className={`form-control ${companyErrors?.contacts?.[index]?.lastName ? 'is-invalid' : ''}`}
                                id={`contact-lastName-${index}`}
                                name={`contacts[${index}].lastName`}
                                placeholder="Enter last name"
                                value={c.lastName ?? ''}
                                onChange={(e) => handleContactInputChange(index, 'lastName', e.target.value)}
                                autoComplete="family-name"
                              />
                              {companyErrors?.contacts?.[index]?.lastName && (
                                <div className="invalid-feedback">{companyErrors.contacts?.[index]?.lastName}</div>
                              )}
                            </div>
                          </div>

                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Email <span className="text-danger">*</span></label>
                            <input
                              type="email"
                              className={`form-control ${companyErrors?.contacts?.[index]?.email ? 'is-invalid' : ''}`}
                              id={`contact-email-${index}`}
                              name={`contacts[${index}].email`}
                              placeholder="Email"
                              value={c.email ?? ''}
                              onChange={(e) => handleContactInputChange(index, 'email', e.target.value)}
                              autoComplete="email"
                              required
                            />
                            {companyErrors?.contacts?.[index]?.email && (
                              <div className="invalid-feedback">{companyErrors.contacts?.[index]?.email}</div>
                            )}
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Phone</label>
                            <input
                              type="text"
                              className="form-control"
                              id={`contact-phone-${index}`}
                              name={`contacts[${index}].phone`}
                              placeholder="Phone"
                              value={c.phone ?? ''}
                              onChange={(e) => handleContactInputChange(index, 'phone', e.target.value)}
                              autoComplete="tel"
                            />
                          </div>

                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Position <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className={`form-control ${companyErrors?.contacts?.[index]?.position ? 'is-invalid' : ''}`}
                              id={`contact-position-${index}`}
                              name={`contacts[${index}].position`}
                              placeholder="Enter position"
                              value={c.position ?? ''}
                              onChange={(e) => handleContactInputChange(index, 'position', e.target.value)}
                              autoComplete="organization-title"
                            />
                            {companyErrors?.contacts?.[index]?.position && (
                              <div className="invalid-feedback">{companyErrors.contacts?.[index]?.position}</div>
                            )}
                          </div>

                          <div className="col-lg-6">
                            <div className="mb-3">
                              <label className="form-label">Allow Login Access</label>
                              <label className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`contact-login-${index}`}
                                  name={`contacts[${index}].allowLogin`}
                                  checked={c.allowLogin ?? false}
                                  onChange={(e) => handleContactLoginToggle(index, e.target.checked)}
                                />
                                <span className="form-check-label">Enable user account login</span>
                              </label>
                            </div>
                          </div>

                          {(c.allowLogin ?? false) && (
                            <>
                              <div className="col-lg-6">
                                <div className="mb-3">
                                  <label className="form-label">Generate Random Password</label>
                                  <label className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`contact-generate-password-${index}`}
                                      name={`contacts[${index}].generatePassword`}
                                      checked={c.generatePassword ?? false}
                                      onChange={(e) => handleGeneratePasswordToggle(index, e.target.checked)}
                                    />
                                    <span className="form-check-label">Auto-generate secure password</span>
                                  </label>
                                  {(c.generatePassword ?? false) && (
                                    <div className="form-hint text-success">
                                      A secure password will be generated and sent to the user's email
                                    </div>
                                  )}
                                </div>
                              </div>

                              {!(c.generatePassword ?? false) && (
                                <>
                                  <div className="col-lg-6">
                                    <div className="mb-3">
                                      <label className="form-label">Password</label>
                                      <div className="input-group">
                                        <input
                                          type={showPassword[index] ? 'text' : 'password'}
                                          className={`form-control ${companyErrors?.contacts?.[index]?.password ? 'is-invalid' : ''}`}
                                          id={`contact-password-${index}`}
                                          name={`contacts[${index}].password`}
                                          value={c.password ?? ''}
                                          onChange={(e) => handleContactInputChange(index, 'password', e.target.value)}
                                          placeholder="Enter password"
                                          autoComplete="new-password"
                                        />
                                        <button
                                          type="button"
                                          className="input-group-text"
                                          onClick={() => {
                                            const newShowPassword = [...showPassword];
                                            newShowPassword[index] = !newShowPassword[index];
                                            setShowPassword(newShowPassword);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          {showPassword[index] ? (
                                            <EyeOff size={16} />
                                          ) : (
                                            <Eye size={16} />
                                          )}
                                        </button>
                                      </div>
                                      {companyErrors?.contacts?.[index]?.password && (
                                        <div className="invalid-feedback">{companyErrors.contacts?.[index]?.password}</div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="col-lg-6">
                                    <div className="mb-3">
                                      <label htmlFor={`contact-confirm-password-${index}`} className="form-label">Confirm Password</label>
                                      <div className="input-group">
                                        <input
                                          type={showConfirmPassword[index] ? 'text' : 'password'}
                                          className={`form-control ${companyErrors?.contacts?.[index]?.confirmPassword ? 'is-invalid' : ''}`}
                                          id={`contact-confirm-password-${index}`}
                                          name={`contacts[${index}].confirmPassword`}
                                          value={c.confirmPassword ?? ''}
                                          onChange={(e) => handleContactInputChange(index, 'confirmPassword', e.target.value)}
                                          placeholder="Confirm password"
                                          autoComplete="new-password"
                                        />
                                        <button
                                          type="button"
                                          className="input-group-text"
                                          onClick={() => {
                                            const newShowConfirmPassword = [...showConfirmPassword];
                                            newShowConfirmPassword[index] = !newShowConfirmPassword[index];
                                            setShowConfirmPassword(newShowConfirmPassword);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          {showConfirmPassword[index] ? (
                                            <EyeOff size={16} />
                                          ) : (
                                            <Eye size={16} />
                                          )}
                                        </button>
                                      </div>
                                      {companyErrors?.contacts?.[index]?.confirmPassword && (
                                        <div className="invalid-feedback">{companyErrors.contacts?.[index]?.confirmPassword}</div>
                                      )}
                                      {c.password && c.confirmPassword && c.password !== c.confirmPassword && (
                                        <div className="invalid-feedback d-block">Passwords do not match</div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ); })}
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddContact}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="inv-footer">
              <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                {loading ? 'Saving company…' : (
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                )}
              </span>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-inv-save" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Saving…
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Save
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
};

export default CompanyEdit;

